import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { format } from 'date-fns';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowDownRight,
  ArrowUpRight,
  DollarSign,
  Globe,
  Pencil,
  Plus,
  RefreshCw,
  Target,
  Trash2,
  TrendingUp,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { db } from '../firebase';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop } from '../components/crm/reportCards';
import { useTheme } from '../contexts/ThemeContext';
import {
  GLASS_PALETTE,
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_SECONDARY_BUTTON,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassCircleButton,
  GlassFigure,
  GlassBadge,
  GlassAccentButton,
  GlassAvatar,
  SlidingPills,
  glassChartTheme,
} from '../components/glass/GlassUi';
import {
  addMarketingReportEntry,
  deleteMarketingReportEntry,
  updateMarketingReportEntry,
  DATA_TYPES,
} from '../services/marketingReportsService';
import { getSharedNewsletterData } from '../services/newsletterService';
import {
  SOCIAL_PLATFORMS,
  TIMEFRAMES,
  adSeries,
  adSpendOf,
  bucketUnit,
  channelFor,
  dedupeDaily,
  getRange,
  groupLeadsBySource,
  inRange,
  leadSeries,
  normalizeLead,
  socialSeries,
  socialSummary,
  stageBreakdown,
  summarizeAds,
  summarizeLeads,
  summarizeWebsite,
  toDate,
  websiteSeries,
} from '../utils/marketingReportMetrics';

// ── Palette ─────────────────────────────────────────────────────────────────
const C = {
  leads: GLASS_TONE.red,
  qualified: GLASS_TONE.blue,
  clients: GLASS_TONE.mint,
  open: GLASS_TONE.blue,
  lost: GLASS_TONE.red,
  visitors: GLASS_TONE.blue,
  sessions: GLASS_TONE.lavender,
  spend: GLASS_TONE.amber,
  clicks: GLASS_TONE.coral,
  average: GLASS_TONE.stone,
};

const STAGE_COLORS = {
  Prospects: GLASS_TONE.lavender,
  'Lead Received': GLASS_TONE.blue,
  'Discovery Call': GLASS_PALETTE[6],
  Assessment: GLASS_PALETTE[8],
  'Onboarding Call': GLASS_TONE.amber,
  Proposal: GLASS_PALETTE[5],
  Client: GLASS_TONE.mint,
  'Former client': GLASS_PALETTE[10],
  Lost: GLASS_TONE.red,
};

// Badge tone per stage (GlassBadge tones are spelled-out classes)
const STAGE_TONE = {
  Prospects: 'lavender',
  'Lead Received': 'blue',
  'Discovery Call': 'blue',
  Assessment: 'amber',
  'Onboarding Call': 'amber',
  Proposal: 'coral',
  Client: 'mint',
  'Former client': 'stone',
  Lost: 'red',
};

const PLATFORM_META = {
  Facebook: { icon: '/facebook.svg', color: GLASS_PALETTE[6], href: 'https://www.facebook.com/p/Builders-Breakthrough-Advisors-61556185584027/' },
  Instagram: { icon: '/instagram.svg', color: GLASS_PALETTE[5], href: 'https://www.instagram.com/buildersbreakthroughadvisors/?hl=en' },
  LinkedIn: { icon: '/Linkedin.svg', color: GLASS_TONE.blue, href: 'https://www.linkedin.com/company/builders-breakthrough-advisors/?viewAsMember=true' },
  YouTube: { icon: '/Youtube.svg', color: GLASS_TONE.red, href: 'https://www.youtube.com/@BuildersBreakthroughAdvisors' },
  TikTok: { icon: '/tiktok.svg', color: GLASS_PALETTE[8], href: 'https://www.tiktok.com/@builders_breakthrough' },
};

// ── Formatting ──────────────────────────────────────────────────────────────
const fmtInt = (n) => Math.round(n || 0).toLocaleString();
const fmtMoney = (n, digits) => {
  const d = digits ?? (Math.abs(n) < 100 ? 2 : 0);
  return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};
const fmtPct = (fraction, digits = 1) => `${((fraction || 0) * 100).toFixed(digits)}%`;
const fmtDuration = (sec) => {
  if (!sec) return '0s';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};
const fmtDate = (d) => (d ? format(d, 'MMM d, yyyy') : '—');

const emptySocialForm = () => ({
  date: format(new Date(), 'yyyy-MM-dd'),
  Facebook: '',
  Instagram: '',
  LinkedIn: '',
  YouTube: '',
  TikTok: '',
});

// ── Small building blocks ───────────────────────────────────────────────────

const MUTED = 'text-gray-500 dark:text-white/[0.45]';
const LABEL = 'mb-1.5 block text-sm font-medium text-gray-600 dark:text-white/60';

const PILL_TONES = {
  mint: 'bg-[#7FD9B0]/25 text-emerald-800 dark:bg-[#7FD9B0]/[0.15] dark:text-[#7FD9B0]',
  red: 'bg-[#F80707]/10 text-[#D10606] dark:bg-[#F80707]/[0.18] dark:text-[#FF6B5E]',
  blue: 'bg-[#5BC0EB]/20 text-[#17698F] dark:bg-[#5BC0EB]/[0.15] dark:text-[#5BC0EB]',
};

/** Period-over-period change, shared by the header KPIs and the inline pills. `invert` = lower is better (costs). */
const deltaOf = (current, previous, invert = false) => {
  if (previous == null) return null;
  if (!previous && !current) return { text: 'No change', tone: null };
  if (!previous) return { text: 'New', tone: 'blue' };
  const pct = (current - previous) / previous;
  const up = pct >= 0;
  const good = invert ? !up : up;
  return {
    up,
    tone: good ? 'mint' : 'red',
    text: `${Math.abs(pct * 100).toFixed(pct !== 0 && Math.abs(pct) < 0.1 ? 1 : 0)}%`,
  };
};

/** Period-over-period pill. `invert` = lower is better (costs). */
const DeltaPill = ({ current, previous, invert = false }) => {
  const d = deltaOf(current, previous, invert);
  if (!d) return null;
  if (!d.tone) return <span className={`text-[11px] ${MUTED}`}>{d.text}</span>;
  if (d.text === 'New') {
    return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${PILL_TONES.blue}`}>New</span>;
  }
  const Icon = d.up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${PILL_TONES[d.tone]}`}
      title={`Previous period: ${previous.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
    >
      <Icon className="h-3 w-3" />
      {d.text}
    </span>
  );
};

/** Header KPI: GlassStat props, with the period delta as its badge */
const kpiStat = ({ icon, label, value, sub, current, previous, invert }) => {
  const d = deltaOf(current, previous, invert);
  return {
    icon,
    value,
    label: sub ? `${label} · ${sub}` : label,
    badge: d ? `${d.up === undefined ? '' : d.up ? '↑ ' : '↓ '}${d.text}` : undefined,
    badgeTone: d?.tone || 'neutral',
  };
};

/** A pinnable report card: `id` is its pin id, `size` its width in My view */
const Section = ({ id, size, title, subtitle, actions, delay = 0, children, className = '' }) => (
  <PinnableCard id={id} title={title} size={size}>
    <GlassCard as="section" className={`p-5 ${className}`} delay={delay}>
      <GlassCardHeader title={title} right={actions} />
      {subtitle && <p className={`mt-1 text-xs ${MUTED}`}>{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </GlassCard>
  </PinnableCard>
);

/** Stat tiles strip */
const StatStrip = ({ items }) => (
  <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
    {items.map((it) => (
      <div key={it.label} className={`${GLASS_INSET} px-4 py-3`}>
        <div className={`text-[11px] ${MUTED}`}>{it.label}</div>
        <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-white">{it.value}</div>
        {it.hint && <div className="mt-0.5 text-[11px] text-gray-400 dark:text-white/40">{it.hint}</div>}
      </div>
    ))}
  </div>
);

const IconButton = ({ onClick, title, tone = 'gray', children }) => (tone === 'red' ? (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-[#F80707] text-white shadow-[0_8px_24px_-10px_rgba(248,7,7,0.75)] transition hover:bg-[#C90505] active:scale-[0.98]"
  >
    {children}
  </button>
) : (
  <GlassCircleButton onClick={onClick} title={title}>{children}</GlassCircleButton>
));

const EmptyChart = ({ children, className = 'h-64' }) => (
  <div className={`flex flex-col items-center justify-center rounded-2xl border border-dashed border-black/10 text-center text-sm text-gray-500 dark:border-white/10 dark:text-white/40 ${className}`}>
    {children}
  </div>
);

/** Glass tooltip with per-series formatters and a payload-driven title. */
const ChartTooltip = ({ active, payload, label, formatters = {}, labelKey = 'tooltipLabel' }) => {
  if (!active || !payload?.length) return null;
  const title = payload[0]?.payload?.[labelKey] || label;
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white/[0.85] px-3 py-2 text-xs shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-[#14110f]/90">
      <div className="mb-1 text-gray-500 dark:text-white/[0.45]">{title}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color || p.stroke || p.fill }} />
          <span className="text-gray-700 dark:text-white/75">{p.name}</span>
          <span className="ml-auto pl-3 font-semibold tabular-nums text-gray-900 dark:text-white">
            {(formatters[p.dataKey] || fmtInt)(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

/** Legend chip (GlassChip look) with an optional dashed swatch for reference lines */
const LegendDot = ({ color, label, value, dashed }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-black/[0.05] bg-black/[0.04] px-3 py-1.5 text-xs dark:border-white/[0.06] dark:bg-white/[0.06]">
    {dashed ? (
      <span className="w-4 border-t-2 border-dashed" style={{ borderColor: color }} />
    ) : (
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
    )}
    <span className="font-medium text-gray-800 dark:text-white/[0.85]">{label}</span>
    {value != null && <span className="tabular-nums text-gray-500 dark:text-white/[0.45]">{value}</span>}
  </span>
);

const Segmented = ({ options, value, onChange, size = 'md', ariaLabel }) => (
  <SlidingPills
    size={size}
    options={options.map((o) => ({ value: o.key, label: o.label }))}
    value={value}
    onChange={onChange}
    ariaLabel={ariaLabel}
  />
);

const Modal = ({ title, onClose, children, wide }) => (
  <div className={GLASS_MODAL_OVERLAY} onClick={onClose}>
    <div
      className={`${GLASS_MODAL_PANEL} ${wide ? 'max-w-3xl' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative mb-5 flex items-center justify-center">
        <h3 className="text-center text-lg font-medium text-gray-900 dark:text-white">{title}</h3>
        <button onClick={onClose} className="absolute right-0 rounded-full p-1.5 text-gray-500 transition hover:bg-black/[0.05] hover:text-[#F80707] dark:text-white/50 dark:hover:bg-white/[0.06] dark:hover:text-[#FF4D40]" aria-label="Close">
          <X className="h-5 w-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

// ── Page ────────────────────────────────────────────────────────────────────

const MarketingReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [timeframe, setTimeframe] = useState('year');
  const [sourceMode, setSourceMode] = useState('source');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const [leads, setLeads] = useState([]);
  const [websiteRows, setWebsiteRows] = useState([]);
  const [adRows, setAdRows] = useState([]);
  const [socialEntries, setSocialEntries] = useState([]);
  const [newsletterCampaigns, setNewsletterCampaigns] = useState([]);

  const [showSocialPopup, setShowSocialPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [socialFormData, setSocialFormData] = useState(emptySocialForm);
  const [editingData, setEditingData] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      setError(null);
      // Leads + clients (conversion is in place on the same doc) and every
      // marketingReports row — website/ads are written globally by webhooks and
      // social snapshots must be visible to every internal user, not just the
      // person who typed them in.
      const [accountsSnap, reportsSnap, newsletter] = await Promise.all([
        getDocs(query(collection(db, 'accounts'), where('type', 'in', ['Leads', 'Clients']))),
        getDocs(collection(db, 'marketingReports')),
        getSharedNewsletterData().catch(() => ({ campaigns: [] })),
      ]);

      setLeads(accountsSnap.docs.map((d) => normalizeLead({ id: d.id, ...d.data() })));

      const rows = reportsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setWebsiteRows(dedupeDaily(rows.filter((r) => r.dataType === DATA_TYPES.WEBSITE)));
      setAdRows(dedupeDaily(rows.filter((r) => r.dataType === DATA_TYPES.ADVERTISING)));
      setSocialEntries(rows.filter((r) => r.dataType === DATA_TYPES.SOCIAL));
      setNewsletterCampaigns(newsletter.campaigns || []);
      setLoadedAt(new Date());
    } catch (err) {
      console.error('Error loading marketing report data:', err);
      setError('Failed to load marketing data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived metrics ──
  const datedLeads = useMemo(() => leads.filter((l) => l.createdAt), [leads]);
  const undatedLeadCount = leads.length - datedLeads.length;

  const range = useMemo(() => {
    const earliest = datedLeads.reduce((min, l) => (!min || l.createdAt < min ? l.createdAt : min), null);
    return getRange(timeframe, new Date(), earliest);
  }, [timeframe, datedLeads]);

  const unit = bucketUnit(range.start, range.end);
  const hasPrev = !!range.prevStart;

  const leadsNow = useMemo(() => datedLeads.filter((l) => inRange(l.createdAt, range.start, range.end)), [datedLeads, range]);
  const leadsPrev = useMemo(
    () => (hasPrev ? datedLeads.filter((l) => inRange(l.createdAt, range.prevStart, range.prevEnd)) : []),
    [datedLeads, range, hasPrev],
  );
  const leadStats = useMemo(() => summarizeLeads(leadsNow), [leadsNow]);
  const leadStatsPrev = useMemo(() => summarizeLeads(leadsPrev), [leadsPrev]);
  const leadChart = useMemo(() => leadSeries(leadsNow, range.start, range.end, unit), [leadsNow, range, unit]);
  const avgLeadsPerBucket = leadChart.length ? leadStats.total / leadChart.length : 0;
  const sourceRows = useMemo(() => groupLeadsBySource(leadsNow, sourceMode), [leadsNow, sourceMode]);
  const stages = useMemo(() => stageBreakdown(leadsNow), [leadsNow]);
  const recentLeads = useMemo(
    () => [...leadsNow].sort((a, b) => b.createdAt - a.createdAt).slice(0, 8),
    [leadsNow],
  );
  const peakBucket = useMemo(
    () => leadChart.reduce((best, b) => (b.leads > (best?.leads || 0) ? b : best), null),
    [leadChart],
  );

  const webNow = useMemo(() => websiteRows.filter((r) => inRange(r.day, range.start, range.end)), [websiteRows, range]);
  const webPrev = useMemo(
    () => (hasPrev ? websiteRows.filter((r) => inRange(r.day, range.prevStart, range.prevEnd)) : []),
    [websiteRows, range, hasPrev],
  );
  const web = useMemo(() => summarizeWebsite(webNow), [webNow]);
  const webPrevStats = useMemo(() => summarizeWebsite(webPrev), [webPrev]);
  const webChart = useMemo(() => websiteSeries(webNow, range.start, range.end, unit), [webNow, range, unit]);
  const lastWebDay = websiteRows.length ? websiteRows[websiteRows.length - 1].day : null;

  const adsNow = useMemo(() => adRows.filter((r) => inRange(r.day, range.start, range.end)), [adRows, range]);
  const adsPrev = useMemo(
    () => (hasPrev ? adRows.filter((r) => inRange(r.day, range.prevStart, range.prevEnd)) : []),
    [adRows, range, hasPrev],
  );
  const ads = useMemo(() => summarizeAds(adsNow), [adsNow]);
  const adsPrevStats = useMemo(() => summarizeAds(adsPrev), [adsPrev]);
  const adChart = useMemo(() => adSeries(adsNow, range.start, range.end, unit), [adsNow, range, unit]);
  const lastSpendRow = useMemo(() => [...adRows].reverse().find((r) => adSpendOf(r) > 0) || null, [adRows]);

  // Cost per Meta lead: ad spend only buys Facebook/Instagram/Meta leads, so
  // dividing by every lead (referrals, events…) would understate it.
  const isMetaLead = (l) => /facebook|instagram|meta/i.test(l.source) || channelFor(l.source) === 'Paid Social';
  const metaLeadsNow = leadsNow.filter(isMetaLead).length;
  const metaLeadsPrev = leadsPrev.filter(isMetaLead).length;
  const cplNow = ads.spend > 0 && metaLeadsNow > 0 ? ads.spend / metaLeadsNow : null;
  const cplPrev = adsPrevStats.spend > 0 && metaLeadsPrev > 0 ? adsPrevStats.spend / metaLeadsPrev : null;

  const social = useMemo(() => socialSummary(socialEntries, timeframe === 'all' ? null : range.start, range.end), [socialEntries, range, timeframe]);
  const socialChart = useMemo(() => socialSeries(socialEntries), [socialEntries]);
  const totalFollowers = social.reduce((s, p) => s + (p.followers || 0), 0);
  const followerChange = social.reduce((s, p) => s + (p.change || 0), 0);
  const lastSocialDay = social.reduce((max, p) => (p.asOf && (!max || p.asOf > max) ? p.asOf : max), null);

  const sentCampaigns = newsletterCampaigns.filter((c) => (parseInt(c.sent, 10) || 0) > 0);
  const avgMetric = (key) => (sentCampaigns.length
    ? sentCampaigns.reduce((s, c) => s + (parseFloat(c.metrics?.[key]) || 0), 0) / sentCampaigns.length
    : null);

  const rangeLabel = `${format(range.start, 'MMM d, yyyy')} – ${format(range.end, 'MMM d, yyyy')}`;
  const prevLabel = hasPrev ? `vs ${format(range.prevStart, 'MMM d')} – ${format(range.prevEnd, 'MMM d, yyyy')}` : null;
  const unitLabel = unit === 'day' ? 'daily' : unit === 'week' ? 'weekly' : 'monthly';
  const prev = (v) => (hasPrev ? v : null);

  const chart = glassChartTheme(darkMode);
  const axisTick = { fontSize: 11, fill: chart.tick };
  const gridStroke = chart.grid;
  const cursorFill = chart.cursor;

  // ── Social entry handlers ──
  const handleSocialSubmit = async (e) => {
    e.preventDefault();
    const filled = SOCIAL_PLATFORMS.filter((p) => String(socialFormData[p] ?? '').trim() !== '');
    if (!filled.length) {
      setError('Enter a follower count for at least one platform.');
      return;
    }
    try {
      const day = toDate(socialFormData.date);
      const month = format(day, 'MMMM');
      const year = format(day, 'yyyy');
      const created = await Promise.all(filled.map((platform) => addMarketingReportEntry(DATA_TYPES.SOCIAL, {
        platform,
        followers: String(socialFormData[platform]).trim(),
        date: socialFormData.date,
        month,
        year,
      })));
      setSocialEntries((prevEntries) => [...created, ...prevEntries]);
      setError(null);
      setShowSocialPopup(false);
      setSocialFormData(emptySocialForm());
    } catch (err) {
      console.error('Error saving social data:', err);
      setError('Failed to save social data. Please try again.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // eslint-disable-next-line no-unused-vars
      const { id, userId, dataType, createdAt, updatedAt, ...updateData } = editingData;
      await updateMarketingReportEntry(id, updateData);
      setSocialEntries((prevEntries) => prevEntries.map((x) => (x.id === id ? { ...x, ...updateData } : x)));
      setEditingData(null);
    } catch (err) {
      console.error('Error updating entry:', err);
      setError('Failed to update entry. Please try again.');
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Delete the ${entry.platform} snapshot from ${fmtDate(toDate(entry.date))}?`)) return;
    try {
      await deleteMarketingReportEntry(entry.id);
      setSocialEntries((prevEntries) => prevEntries.filter((x) => x.id !== entry.id));
    } catch (err) {
      console.error('Error deleting entry:', err);
      setError('Failed to delete entry. Please try again.');
    }
  };

  const sortedSocialEntries = useMemo(
    () => [...socialEntries].sort((a, b) => (toDate(b.date) || 0) - (toDate(a.date) || 0) || a.platform.localeCompare(b.platform)),
    [socialEntries],
  );

  const sourceChartHeight = Math.max(160, sourceRows.length * 38 + 30);

  const stats = loading ? [] : [
    kpiStat({ icon: Users, label: 'New Leads', value: fmtInt(leadStats.total), current: leadStats.total, previous: prev(leadStatsPrev.total) }),
    kpiStat({
      icon: UserCheck, label: 'Qualified', value: fmtInt(leadStats.qualified),
      sub: `${fmtPct(leadStats.qualifiedRate, 0)} of leads`,
      current: leadStats.qualified, previous: prev(leadStatsPrev.qualified),
    }),
    kpiStat({
      icon: Target, label: 'Became Clients', value: fmtInt(leadStats.clients),
      sub: `${fmtPct(leadStats.conversionRate, 0)} conversion`,
      current: leadStats.clients, previous: prev(leadStatsPrev.clients),
    }),
    kpiStat({
      icon: Globe, label: 'Website Visitors', value: fmtInt(web.visitors),
      sub: `${fmtPct(web.engagementRate, 0)} engaged`,
      current: web.visitors, previous: prev(webPrevStats.visitors),
    }),
    kpiStat({
      icon: DollarSign, label: 'Ad Spend', value: fmtMoney(ads.spend),
      sub: ads.clicks ? `${fmtMoney(ads.cpc)} / click` : undefined,
      current: ads.spend, previous: prev(adsPrevStats.spend),
    }),
    kpiStat({
      icon: TrendingUp, label: 'Cost / Meta Lead', value: cplNow != null ? fmtMoney(cplNow) : '—',
      sub: cplNow != null ? `${metaLeadsNow} Meta lead${metaLeadsNow === 1 ? '' : 's'}` : 'No paid spend',
      current: cplNow, previous: cplNow != null && hasPrev ? cplPrev : null, invert: true,
    }),
  ];

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      {/* Header */}
      <GlassPageHeader
        onBack={() => navigate('/crm/reports')}
        backTitle="Back to reports"
        title={<>Marketing<br />Reports</>}
        stats={stats}
        statsKey={timeframe}
        controls={(
          <>
            <Segmented size="sm" options={TIMEFRAMES} value={timeframe} onChange={setTimeframe} ariaLabel="Timeframe" />
            <IconButton onClick={() => load({ silent: true })} title="Refresh data">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
          </>
        )}
      />
      <p className={`-mt-4 mb-4 text-sm ${MUTED}`}>
        {rangeLabel}
        {prevLabel && <span className="hidden sm:inline"> · {prevLabel}</span>}
      </p>

      {error && (
        <div className={`mb-4 flex items-center justify-between gap-3 rounded-2xl border border-[#F80707]/20 bg-[#F80707]/[0.06] px-4 py-3 text-sm dark:border-[#F80707]/30 dark:bg-[#F80707]/10 ${GLASS_TEXT.red}`} role="alert">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}

      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <BBALoader size={96} />
        </div>
      ) : (
        // Cards replay their rise whenever the timeframe changes
        <div key={timeframe} className="space-y-4">
          {/* Total Leads — full width */}
          <Section
            id="total-leads"
            size="wide"
            title="Total Leads"
            subtitle={`New leads by created date, ${unitLabel}. Includes leads that have since become clients.`}
            delay={0}
          >
            <GlassFigure value={fmtInt(leadStats.total)} caption="new leads" />
            <div className="mb-4 mt-4 flex flex-wrap items-center gap-2">
              <LegendDot color={C.leads} label="Leads" value={fmtInt(leadStats.total)} />
              <LegendDot color={C.qualified} label="Qualified" value={fmtInt(leadStats.qualified)} />
              <LegendDot color={C.clients} label="Became clients" value={fmtInt(leadStats.clients)} />
              <LegendDot color={C.average} dashed label={`Avg / ${unit}`} value={avgLeadsPerBucket.toFixed(1)} />
              {peakBucket && (
                <span className={`ml-1 text-xs ${MUTED}`}>
                  Peak: <span className="font-semibold text-gray-900 dark:text-white">{peakBucket.tooltipLabel}</span> ({peakBucket.leads})
                </span>
              )}
            </div>
            {leadStats.total > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={leadChart} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="mrLeadsFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.leads} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={C.leads} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="mrQualFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.qualified} stopOpacity={0.2} />
                        <stop offset="100%" stopColor={C.qualified} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={gridStroke} strokeDasharray="4 6" />
                    <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={40} />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: gridStroke }} />
                    <ReferenceLine y={avgLeadsPerBucket} stroke={C.average} strokeDasharray="5 5" />
                    <Area type="monotone" dataKey="leads" name="Leads" stroke={C.leads} strokeWidth={2.5} fill="url(#mrLeadsFill)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} animationDuration={800} />
                    <Area type="monotone" dataKey="qualified" name="Qualified" stroke={C.qualified} strokeWidth={2} fill="url(#mrQualFill)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} animationDuration={800} />
                    <Line type="monotone" dataKey="clients" name="Became clients" stroke={C.clients} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} animationDuration={800} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyChart className="h-72">No leads were created in this period.</EmptyChart>
            )}
            {undatedLeadCount > 0 && (
              <p className="mt-3 text-center text-[11px] text-gray-400 dark:text-white/30">
                {undatedLeadCount} account{undatedLeadCount === 1 ? ' has' : 's have'} no created date and {undatedLeadCount === 1 ? 'is' : 'are'} excluded.
              </p>
            )}
          </Section>

          {/* Lead Source — full width */}
          <Section
            id="lead-source"
            size="wide"
            title="Lead Source"
            subtitle="Where this period's leads came from, split by where they are now"
            delay={80}
            actions={(
              <Segmented
                size="sm"
                value={sourceMode}
                onChange={setSourceMode}
                options={[{ key: 'source', label: 'Source' }, { key: 'channel', label: 'Channel' }]}
                ariaLabel="Group by"
              />
            )}
          >
            {sourceRows.length > 0 ? (
              <>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <LegendDot color={C.clients} label="Became client" />
                  <LegendDot color={C.open} label="Still open" />
                  <LegendDot color={C.lost} label="Lost" />
                </div>
                <div style={{ height: sourceChartHeight }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sourceRows} layout="vertical" margin={{ top: 0, right: 48, left: 0, bottom: 0 }} barCategoryGap={8}>
                      <CartesianGrid horizontal={false} stroke={gridStroke} strokeDasharray="4 6" />
                      <XAxis type="number" allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={210}
                        tick={{ ...axisTick, fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => (v.length > 30 ? `${v.slice(0, 29)}…` : v)}
                      />
                      <Tooltip
                        cursor={{ fill: cursorFill }}
                        content={<ChartTooltip labelKey="name" />}
                      />
                      <Bar dataKey="clients" name="Became client" stackId="s" fill={C.clients} radius={6} background={{ fill: chart.track, radius: 8 }} animationDuration={700} />
                      <Bar dataKey="open" name="Still open" stackId="s" fill={C.open} radius={6} animationDuration={700} />
                      <Bar dataKey="lost" name="Lost" stackId="s" fill={C.lost} radius={6} animationDuration={700}>
                        <LabelList dataKey="leads" position="right" fill={darkMode ? '#FFFFFF' : '#1C1916'} fontSize={12} fontWeight={600} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr>
                        <th className={GLASS_TH}>{sourceMode === 'channel' ? 'Channel' : 'Source'}</th>
                        <th className={`${GLASS_TH} text-right`}>Leads</th>
                        <th className={`${GLASS_TH} text-right`}>Share</th>
                        <th className={`${GLASS_TH} text-right`}>Qualified</th>
                        <th className={`${GLASS_TH} text-right`}>Clients</th>
                        <th className={`${GLASS_TH} text-right`}>Conversion</th>
                        <th className={`${GLASS_TH} pr-0 text-right`}>Lost</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-700 dark:text-white/75">
                      {sourceRows.map((r) => (
                        <tr key={r.name} className={GLASS_ROW}>
                          <td className="py-3 pr-4 font-medium text-gray-900 dark:text-white">{r.name}</td>
                          <td className="py-3 pr-4 text-right font-semibold tabular-nums text-gray-900 dark:text-white">{r.leads}</td>
                          <td className="py-3 pr-4 text-right">
                            <div className="inline-flex items-center justify-end gap-2">
                              <span className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.07] md:block">
                                <span className="block h-full rounded-full bg-[#F80707]" style={{ width: `${r.share * 100}%` }} />
                              </span>
                              <span className="w-12 tabular-nums">{fmtPct(r.share, 0)}</span>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">{r.qualified} <span className="text-gray-400 dark:text-white/30">({fmtPct(r.qualifiedRate, 0)})</span></td>
                          <td className="py-3 pr-4 text-right tabular-nums">{r.clients}</td>
                          <td className={`py-3 pr-4 text-right font-semibold tabular-nums ${r.conversionRate > 0 ? GLASS_TEXT.mint : 'text-gray-400 dark:text-white/30'}`}>{fmtPct(r.conversionRate, 0)}</td>
                          <td className={`py-3 text-right tabular-nums ${MUTED}`}>{r.lost}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-black/10 font-semibold text-gray-900 dark:border-white/10 dark:text-white">
                        <td className="py-3 pr-4">Total</td>
                        <td className="py-3 pr-4 text-right tabular-nums">{leadStats.total}</td>
                        <td className="py-3 pr-4 text-right tabular-nums">100%</td>
                        <td className="py-3 pr-4 text-right tabular-nums">{leadStats.qualified} <span className="font-normal text-gray-400 dark:text-white/30">({fmtPct(leadStats.qualifiedRate, 0)})</span></td>
                        <td className="py-3 pr-4 text-right tabular-nums">{leadStats.clients}</td>
                        <td className={`py-3 pr-4 text-right tabular-nums ${GLASS_TEXT.mint}`}>{fmtPct(leadStats.conversionRate, 0)}</td>
                        <td className="py-3 text-right tabular-nums">{leadStats.lost}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            ) : (
              <EmptyChart className="h-48">No leads were created in this period.</EmptyChart>
            )}
          </Section>

          {/* Pipeline + recent leads */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Section id="lead-stages" title="Where These Leads Are Now" subtitle="Current pipeline stage of leads created in this period" delay={140}>
              {stages.length > 0 ? (
                <div className="space-y-3">
                  {stages.map((s) => {
                    const pct = leadStats.total ? s.count / leadStats.total : 0;
                    return (
                      <div key={s.stage} className="flex items-center gap-3">
                        <span className="w-32 shrink-0 truncate text-sm text-gray-700 dark:text-white/75">{s.stage}</span>
                        <div className="h-6 flex-1 overflow-hidden rounded-full bg-black/[0.04] dark:bg-white/[0.035]">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${Math.max(pct * 100, 2)}%`, backgroundColor: STAGE_COLORS[s.stage] || GLASS_TONE.stone }}
                          />
                        </div>
                        <span className="w-20 shrink-0 text-right text-sm tabular-nums">
                          <span className="font-semibold text-gray-900 dark:text-white">{s.count}</span>
                          <span className="ml-1 text-gray-400 dark:text-white/40">{fmtPct(pct, 0)}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyChart className="h-48">No leads in this period.</EmptyChart>
              )}
            </Section>

            <Section id="latest-leads" title="Latest Leads" subtitle="Most recent leads in this period" delay={200}>
              {recentLeads.length > 0 ? (
                <ul className="divide-y divide-black/[0.06] dark:divide-white/[0.06]">
                  {recentLeads.map((l) => (
                    <li key={l.id}>
                      <button
                        type="button"
                        onClick={() => navigate(l.isClient ? `/crm/client/${l.id}` : `/crm/lead/${l.id}`)}
                        className="-mx-2 flex w-full items-center gap-3 rounded-2xl px-2 py-2.5 text-left transition-colors hover:bg-black/[0.025] dark:hover:bg-white/[0.035]"
                      >
                        <GlassAvatar name={l.name} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">{l.name}</span>
                          <span className={`block truncate text-xs ${MUTED}`}>{l.source || 'Unattributed'} · {fmtDate(l.createdAt)}</span>
                        </span>
                        <span className="shrink-0">
                          <GlassBadge tone={STAGE_TONE[l.lost ? 'Lost' : l.stage] || 'neutral'}>{l.lost ? 'Lost' : l.stage}</GlassBadge>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyChart className="h-48">No leads in this period.</EmptyChart>
              )}
            </Section>
          </div>

          {/* Website */}
          <Section
            id="website-traffic"
            size="wide"
            title="Website & Organic Traffic"
            subtitle={`Google Analytics, synced daily${lastWebDay ? ` · latest data ${fmtDate(lastWebDay)}` : ''}`}
            delay={260}
          >
            <StatStrip
              items={[
                { label: 'Visitors', value: fmtInt(web.visitors), hint: hasPrev ? <DeltaPill current={web.visitors} previous={webPrevStats.visitors} /> : null },
                { label: 'New Users', value: fmtInt(web.newUsers), hint: web.visitors ? `${fmtPct(web.newUsers / web.visitors, 0)} of visitors` : null },
                { label: 'Sessions', value: fmtInt(web.sessions), hint: hasPrev ? <DeltaPill current={web.sessions} previous={webPrevStats.sessions} /> : null },
                { label: 'Page Views', value: fmtInt(web.views), hint: web.sessions ? `${web.viewsPerSession.toFixed(1)} / session` : null },
                { label: 'Engagement Rate', value: fmtPct(web.engagementRate), hint: `Bounce ${fmtPct(web.bounceRate)}` },
                { label: 'Avg. Session', value: fmtDuration(web.avgSessionDuration), hint: `${web.days} day${web.days === 1 ? '' : 's'} of data` },
              ]}
            />
            {web.days > 0 ? (
              <>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <LegendDot color={C.visitors} label="Visitors" />
                  <LegendDot color={C.sessions} label="Sessions" />
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={webChart} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="mrWebFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={C.visitors} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={C.visitors} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke={gridStroke} strokeDasharray="4 6" />
                      <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={44} />
                      <Tooltip content={<ChartTooltip />} cursor={{ stroke: gridStroke }} />
                      <Area type="monotone" dataKey="visitors" name="Visitors" stroke={C.visitors} strokeWidth={2.5} fill="url(#mrWebFill)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} animationDuration={800} />
                      <Line type="monotone" dataKey="sessions" name="Sessions" stroke={C.sessions} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} animationDuration={800} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <EmptyChart>No website data was synced for this period.</EmptyChart>
            )}
          </Section>

          {/* Paid ads */}
          <Section
            id="paid-ads"
            size="wide"
            title="Paid Ads"
            subtitle="Meta Ads, synced daily"
            delay={320}
          >
            <StatStrip
              items={[
                { label: 'Spend', value: fmtMoney(ads.spend), hint: `${ads.activeDays} active day${ads.activeDays === 1 ? '' : 's'}` },
                { label: 'Impressions', value: fmtInt(ads.impressions), hint: ads.impressions ? `${fmtMoney(ads.cpm)} CPM` : null },
                { label: 'Clicks', value: fmtInt(ads.clicks), hint: hasPrev ? <DeltaPill current={ads.clicks} previous={adsPrevStats.clicks} /> : null },
                { label: 'CTR', value: fmtPct(ads.ctr, 2), hint: 'clicks ÷ impressions' },
                { label: 'Cost / Click', value: ads.clicks ? fmtMoney(ads.cpc) : '—', hint: 'spend ÷ clicks' },
                { label: 'Cost / Meta Lead', value: cplNow != null ? fmtMoney(cplNow) : '—', hint: `${metaLeadsNow} Facebook/Meta lead${metaLeadsNow === 1 ? '' : 's'}` },
              ]}
            />
            {ads.spend > 0 || ads.clicks > 0 ? (
              <>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <LegendDot color={C.spend} label="Spend" />
                  <LegendDot color={C.clicks} label="Clicks" />
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={adChart} margin={{ top: 10, right: 0, left: -8, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={gridStroke} strokeDasharray="4 6" />
                      <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} interval="preserveStartEnd" />
                      <YAxis yAxisId="spend" tick={axisTick} axisLine={false} tickLine={false} width={52} tickFormatter={(v) => `$${v}`} />
                      <YAxis yAxisId="clicks" orientation="right" allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={40} />
                      <Tooltip content={<ChartTooltip formatters={{ spend: (v) => fmtMoney(v, 2) }} />} cursor={{ fill: cursorFill }} />
                      <Bar yAxisId="spend" dataKey="spend" name="Spend" fill={C.spend} radius={[8, 8, 8, 8]} maxBarSize={36} background={{ fill: chart.track, radius: 8 }} animationDuration={700} />
                      <Line yAxisId="clicks" type="monotone" dataKey="clicks" name="Clicks" stroke={C.clicks} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 0 }} animationDuration={800} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <EmptyChart className="h-40">
                <span>No ad spend recorded in this period.</span>
                {lastSpendRow && (
                  <span className="mt-1 text-xs text-gray-400 dark:text-white/30">Last spend: {fmtDate(lastSpendRow.day)} — switch to All Time to see past campaigns.</span>
                )}
              </EmptyChart>
            )}
          </Section>

          {/* Social */}
          <Section
            id="social-media"
            size="wide"
            title="Social Media"
            subtitle={`Follower snapshots entered by the team${lastSocialDay ? ` · latest ${fmtDate(lastSocialDay)}` : ''}`}
            delay={380}
            actions={(
              <>
                <IconButton onClick={() => { setEditingData(null); setShowEditPopup(true); }} title="Manage entries">
                  <Pencil className="h-4 w-4" />
                </IconButton>
                <IconButton onClick={() => setShowSocialPopup(true)} title="Add follower snapshot" tone="red">
                  <Plus className="h-4 w-4" />
                </IconButton>
              </>
            )}
          >
            <div className="mb-4">
              <GlassFigure value={fmtInt(totalFollowers)} caption="Total Followers" />
              {timeframe !== 'all' && followerChange !== 0 && (
                <div className={`mt-1 text-xs font-semibold ${followerChange > 0 ? GLASS_TEXT.mint : GLASS_TEXT.red}`}>
                  {followerChange > 0 ? '+' : ''}{fmtInt(followerChange)} this period
                </div>
              )}
            </div>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {social.map((p) => {
                const meta = PLATFORM_META[p.platform];
                return (
                  <a
                    key={p.platform}
                    href={meta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${GLASS_INSET} group flex flex-col items-center p-4 text-center transition hover:border-[#F80707]/30 dark:hover:border-[#F80707]/40`}
                  >
                    <img src={meta.icon} alt="" className="mb-2 h-9 w-9" />
                    <span className="text-xs font-medium text-gray-600 dark:text-white/60">{p.platform}</span>
                    <span className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-white">{p.followers != null ? fmtInt(p.followers) : '—'}</span>
                    <span className="mt-0.5 min-h-[16px] text-[11px]">
                      {p.change != null && p.change !== 0 ? (
                        <span className={`font-semibold ${p.change > 0 ? GLASS_TEXT.mint : GLASS_TEXT.red}`}>
                          {p.change > 0 ? '+' : ''}{fmtInt(p.change)}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-white/30">{p.asOf ? `as of ${format(p.asOf, 'MMM d, yy')}` : 'No data'}</span>
                      )}
                    </span>
                  </a>
                );
              })}
            </div>
            {socialChart.length > 1 ? (
              <>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  {SOCIAL_PLATFORMS.filter((p) => socialChart.some((r) => r[p] != null)).map((p) => (
                    <LegendDot key={p} color={PLATFORM_META[p].color} label={p} />
                  ))}
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={socialChart} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={gridStroke} strokeDasharray="4 6" />
                      <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={44} />
                      <Tooltip content={<ChartTooltip labelKey="label" />} cursor={{ stroke: gridStroke }} />
                      {SOCIAL_PLATFORMS.map((p) => (
                        <Line
                          key={p}
                          type="monotone"
                          dataKey={p}
                          name={p}
                          stroke={PLATFORM_META[p].color}
                          strokeWidth={2}
                          dot={{ r: 3, strokeWidth: 0, fill: PLATFORM_META[p].color }}
                          activeDot={{ r: 4, strokeWidth: 0 }}
                          connectNulls
                          animationDuration={800}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <EmptyChart className="h-32">Add follower snapshots over time to see growth trends.</EmptyChart>
            )}
          </Section>

          {/* Newsletter */}
          <Section id="newsletter" size="wide" title="Newsletter" subtitle="Shared newsletter campaigns (all time)" delay={440}>
            <StatStrip
              items={[
                { label: 'Campaigns', value: fmtInt(newsletterCampaigns.length) },
                { label: 'Emails Sent', value: fmtInt(newsletterCampaigns.reduce((s, c) => s + (parseInt(c.sent, 10) || 0), 0)) },
                { label: 'Avg. Open Rate', value: avgMetric('openRate') != null ? `${avgMetric('openRate').toFixed(1)}%` : '—' },
                { label: 'Avg. Click Rate', value: avgMetric('clickRate') != null ? `${avgMetric('clickRate').toFixed(1)}%` : '—' },
                { label: 'Avg. Bounce Rate', value: avgMetric('bounceRate') != null ? `${avgMetric('bounceRate').toFixed(1)}%` : '—' },
                { label: 'Avg. Unsubscribe', value: avgMetric('unsubscribeRate') != null ? `${avgMetric('unsubscribeRate').toFixed(1)}%` : '—' },
              ]}
            />
            {newsletterCampaigns.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className={GLASS_TH}>Campaign</th>
                      <th className={GLASS_TH}>Start</th>
                      <th className={GLASS_TH}>Status</th>
                      <th className={`${GLASS_TH} text-right`}>Sent</th>
                      <th className={`${GLASS_TH} text-right`}>Open</th>
                      <th className={`${GLASS_TH} text-right`}>Click</th>
                      <th className={`${GLASS_TH} pr-0 text-right`}>Unsub.</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-white/75">
                    {[...newsletterCampaigns]
                      .sort((a, b) => (toDate(b.startDate) || 0) - (toDate(a.startDate) || 0))
                      .map((c) => {
                        const sent = parseInt(c.sent, 10) || 0;
                        const metric = (k) => (sent > 0 ? `${(parseFloat(c.metrics?.[k]) || 0).toFixed(1)}%` : '—');
                        return (
                          <tr key={c.id} className={GLASS_ROW}>
                            <td className="py-3 pr-4">
                              <div className="font-medium text-gray-900 dark:text-white">{c.name || 'Untitled campaign'}</div>
                              {c.description && <div className={`max-w-md truncate text-xs ${MUTED}`}>{c.description}</div>}
                            </td>
                            <td className="whitespace-nowrap py-3 pr-4">{fmtDate(toDate(c.startDate))}</td>
                            <td className="py-3 pr-4">
                              <GlassBadge tone={c.status === 'active' ? 'mint' : 'neutral'}>
                                <span className="capitalize">{c.status || 'draft'}</span>
                              </GlassBadge>
                            </td>
                            <td className="py-3 pr-4 text-right font-semibold tabular-nums text-gray-900 dark:text-white">{fmtInt(sent)}</td>
                            <td className="py-3 pr-4 text-right tabular-nums">{metric('openRate')}</td>
                            <td className="py-3 pr-4 text-right tabular-nums">{metric('clickRate')}</td>
                            <td className="py-3 text-right tabular-nums">{metric('unsubscribeRate')}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyChart className="h-32">No newsletter campaigns yet.</EmptyChart>
            )}
            {newsletterCampaigns.length > 0 && sentCampaigns.length === 0 && (
              <p className="mt-3 text-center text-[11px] text-gray-400 dark:text-white/30">Averages appear once a campaign has recorded sends.</p>
            )}
          </Section>

          {loadedAt && (
            <p className="pb-2 text-center text-[11px] text-gray-400 dark:text-white/30">Data loaded {format(loadedAt, 'MMM d, yyyy h:mm a')}</p>
          )}
        </div>
      )}

      {/* Modals stay outside the glass cards: backdrop-filter would trap a fixed overlay inside them */}
      {/* Add social snapshot */}
      {showSocialPopup && (
        <Modal title="Add Follower Snapshot" onClose={() => setShowSocialPopup(false)}>
          <form onSubmit={handleSocialSubmit} className="space-y-4">
            <div>
              <label className={LABEL}>Date</label>
              <input
                type="date"
                value={socialFormData.date}
                onChange={(e) => setSocialFormData({ ...socialFormData, date: e.target.value })}
                className={GLASS_FIELD}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {SOCIAL_PLATFORMS.map((p) => (
                <div key={p} className={p === 'TikTok' ? 'col-span-2' : ''}>
                  <label className={`${LABEL} flex items-center gap-1.5`}>
                    <img src={PLATFORM_META[p].icon} alt="" className="h-4 w-4" />
                    {p}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={socialFormData[p]}
                    onChange={(e) => setSocialFormData({ ...socialFormData, [p]: e.target.value })}
                    className={GLASS_FIELD}
                    placeholder="Followers"
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowSocialPopup(false)} className={`${GLASS_SECONDARY_BUTTON} flex-1 justify-center`}>
                Cancel
              </button>
              <GlassAccentButton type="submit" className="flex-1 justify-center">
                Save Snapshot
              </GlassAccentButton>
            </div>
          </form>
        </Modal>
      )}

      {/* Manage social snapshots */}
      {showEditPopup && (
        <Modal title="Social Media Entries" wide onClose={() => { setShowEditPopup(false); setEditingData(null); }}>
          {sortedSocialEntries.length === 0 ? (
            <p className={`py-6 text-center text-sm ${MUTED}`}>No entries yet. Add your first snapshot with the + button.</p>
          ) : (
            <ul className="space-y-2">
              {sortedSocialEntries.map((entry) => (
                <li key={entry.id} className={`${GLASS_INSET} p-3`}>
                  {editingData?.id === entry.id ? (
                    <form onSubmit={handleEditSubmit} className="grid grid-cols-1 items-end gap-3 sm:grid-cols-4">
                      <div>
                        <label className={LABEL}>Platform</label>
                        <select
                          value={editingData.platform || ''}
                          onChange={(e) => setEditingData({ ...editingData, platform: e.target.value })}
                          className={GLASS_FIELD}
                        >
                          {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={LABEL}>Followers</label>
                        <input
                          type="number"
                          min="0"
                          value={editingData.followers ?? ''}
                          onChange={(e) => setEditingData({ ...editingData, followers: e.target.value })}
                          className={GLASS_FIELD}
                          required
                        />
                      </div>
                      <div>
                        <label className={LABEL}>Date</label>
                        <input
                          type="date"
                          value={editingData.date || ''}
                          onChange={(e) => {
                            const d = toDate(e.target.value);
                            setEditingData({
                              ...editingData,
                              date: e.target.value,
                              month: d ? format(d, 'MMMM') : editingData.month,
                              year: d ? format(d, 'yyyy') : editingData.year,
                            });
                          }}
                          className={GLASS_FIELD}
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <GlassAccentButton type="submit" className="flex-1 justify-center">Save</GlassAccentButton>
                        <button type="button" onClick={() => setEditingData(null)} className={`${GLASS_SECONDARY_BUTTON} flex-1 justify-center`}>Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex items-center gap-3">
                      {PLATFORM_META[entry.platform] && <img src={PLATFORM_META[entry.platform].icon} alt="" className="h-6 w-6" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{entry.platform}</div>
                        <div className={`text-xs ${MUTED}`}>
                          {fmtInt(parseInt(entry.followers, 10) || 0)} followers · {fmtDate(toDate(entry.date))}
                        </div>
                      </div>
                      <button
                        onClick={() => setEditingData({ ...entry, date: entry.date || (toDate(entry.createdAt) ? format(toDate(entry.createdAt), 'yyyy-MM-dd') : '') })}
                        className="rounded-full p-1.5 text-gray-500 transition hover:bg-black/[0.05] hover:text-gray-900 dark:text-white/50 dark:hover:bg-white/[0.06] dark:hover:text-white"
                        aria-label="Edit entry"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteEntry(entry)}
                        className="rounded-full p-1.5 text-[#E00606] transition hover:bg-[#F80707]/10 dark:text-[#FF4D40] dark:hover:bg-[#F80707]/[0.18]"
                        aria-label="Delete entry"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </GlassPage>
  );
};

export default MarketingReports;
